import type { Id, TicketListItem } from '../api/types';

/**
 * 🔴 THE RULE THAT DECIDES WHERE A SUB-TASK IS DRAWN.
 *
 * `GET /api/v1/tickets` returns ONE flat list ordered `updated_at DESC` and has no way to ask for
 * "top-level only" — see the README gap table. So a sub-task arrives in the page exactly like any
 * other ticket, which is why TBS-13/14/15 rendered as three flat rows at the top of the backlog:
 * they were the three most recently updated rows in the whole project.
 *
 * ⭐⭐ THE ORDERING INVERSION IS THE POINT. Once nested, a child's position is decided by its
 * PARENT's position and never by its own `updated_at`. Touching a sub-task must not move it — or its
 * parent — anywhere.
 *
 * 🔴 AND THE PAGINATION HAZARD, STATED EXPLICITLY. A parent can be on page 1 with a child on page 2.
 * Three rules, in this order, and the third is the one that must never be traded away:
 *
 *   1. A root's children come from `GET /tickets?parentId=…` — a SEPARATE request per visible
 *      parent — so the expanded list is complete regardless of which page the children fell on.
 *   2. A child whose parent is on this page is drawn under that parent and NOWHERE ELSE.
 *   3. ⛔ A child whose parent is NOT on this page is still drawn, at the top level, badged as a
 *      sub-task with a link to its parent. It is `detached` — visibly a sub-task shown out of place,
 *      never a silent top-level ticket, and never dropped. A row that vanishes between pages is the
 *      one outcome that is not allowed.
 *
 * ⚠️ Two levels only, and that is enforced in the DATABASE (db/009: a sub-task cannot be a parent),
 * so this file deliberately does not implement an arbitrary tree. `depth` is 0 or 1 by construction.
 */

export type RowKind = 'root' | 'child' | 'detached';

export interface BacklogRow {
  ticket: TicketListItem;
  kind: RowKind;
  /** 0 for anything drawn at the top level — including a `detached` sub-task. 1 for a nested child. */
  depth: 0 | 1;
  /** Roots only. `null` means "we do not know", which is NOT the same as 0 and must not render as 0. */
  childCount: number | null;
  hasChildren: boolean;
  expanded: boolean;
  /**
   * ⚠️ True when `childCount` came from the CURRENT PAGE alone, because the per-parent request has
   * not answered or failed. The number is then a floor — "3 or more" — and the UI renders it as such
   * rather than printing a confident 3 next to a parent that has five.
   */
  countIsFloor: boolean;
  /** True when the rows nested under this one are not guaranteed to be all of its children. */
  childrenIncomplete: boolean;
  /** Set on a nested child so the row can name its parent without a second lookup. */
  parentKey: string | null;
}

export interface SubtaskIndexEntry {
  items: TicketListItem[];
  /**
   * The server's `total` for this parent. ⭐ Kept apart from `items.length` because the request is
   * capped at the API's MAX_PAGE_SIZE — a parent with more children than the cap must report the true
   * count and say the list is truncated, not silently under-report.
   */
  total: number;
}

export type SubtaskIndex = ReadonlyMap<Id, SubtaskIndexEntry>;

/**
 * The ids that are top-level ON THIS PAGE.
 *
 * ⭐ Defined as `parent_id === null` and NOT as "every id on the page". If it were the latter, a
 * child whose parent is itself a child would be suppressed from the top level and then never drawn,
 * because grandchildren are not rendered. The database forbids that shape today; this definition
 * means the UI stays honest if it ever stops forbidding it.
 */
export function rootIdsOf(items: readonly TicketListItem[]): Set<Id> {
  return new Set(items.filter((item) => item.parent_id === null).map((item) => item.id));
}

/**
 * Which parents are worth a `?parentId=` request for this page.
 *
 * ⭐⭐ ONLY the roots that this page already PROVES have a child — normally none or one, so a page of
 * 50 costs 0–1 extra requests, not 50. ⛔ Deliberately not "every root on the page": that is the
 * client-side N+1 the API's own list query goes out of its way to avoid, and it would fire 50
 * requests on every page turn to discover that 49 of them have no children.
 *
 * ⚠️ THE COST OF THAT CHOICE, STATED: a parent whose children ALL fell on another page gets no
 * chevron here, because nothing on this page indicates it has any. Those children are not lost — they
 * are drawn as `detached` rows on the page they landed on. Closing this properly needs `child_count`
 * on the list row; it is in the README gap table.
 */
export function parentIdsToFetch(items: readonly TicketListItem[]): Id[] {
  const roots = rootIdsOf(items);
  const wanted = new Set<Id>();
  for (const item of items) {
    if (item.parent_id !== null && roots.has(item.parent_id)) wanted.add(item.parent_id);
  }
  return [...wanted];
}

function childrenFromPage(items: readonly TicketListItem[], parentId: Id): TicketListItem[] {
  return items.filter((item) => item.parent_id === parentId);
}

interface ChildLookup {
  children: TicketListItem[];
  count: number | null;
  countIsFloor: boolean;
  incomplete: boolean;
}

/** Fetched children win; page-derived children are the fallback when the fetch has not answered. */
function childrenOf(
  items: readonly TicketListItem[],
  parentId: Id,
  subtasks: SubtaskIndex | undefined,
): ChildLookup {
  const fetched = subtasks?.get(parentId);
  if (fetched) {
    return {
      children: fetched.items,
      count: fetched.total,
      countIsFloor: false,
      // The request is capped at the API's page size. An exact count above a shorter list is a real
      // state and is reported as truncated rather than quietly shown as complete.
      incomplete: fetched.items.length < fetched.total,
    };
  }

  const onPage = childrenFromPage(items, parentId);
  if (onPage.length === 0) {
    // ⛔ NOT `count: 0`. "No children on this page" and "no children" are different facts, and only
    // the second one may render as a childless row.
    return { children: [], count: null, countIsFloor: false, incomplete: false };
  }
  return { children: onPage, count: onPage.length, countIsFloor: true, incomplete: true };
}

export interface BuildOptions {
  subtasks?: SubtaskIndex;
  expanded?: ReadonlySet<Id>;
}

/**
 * Flattens one page of tickets into the rows to draw, in draw order.
 *
 * ⭐ The server's order is preserved for the top level and never recomputed here — sorting is the
 * API's job (`updated_at DESC` or `backlog_rank`), and a second, client-side sort is how a list
 * starts disagreeing with its own pagination.
 */
export function buildBacklogRows(
  items: readonly TicketListItem[],
  options: BuildOptions = {},
): BacklogRow[] {
  const { subtasks, expanded } = options;
  const roots = rootIdsOf(items);
  const rows: BacklogRow[] = [];

  for (const item of items) {
    // Rule 2: drawn under its parent, so not here.
    if (item.parent_id !== null && roots.has(item.parent_id)) continue;

    // Rule 3: parent is not on this page — drawn at the top level, badged, never dropped.
    if (item.parent_id !== null) {
      rows.push({
        ticket: item,
        kind: 'detached',
        depth: 0,
        childCount: null,
        hasChildren: false,
        expanded: false,
        countIsFloor: false,
        childrenIncomplete: false,
        parentKey: null,
      });
      continue;
    }

    const lookup = childrenOf(items, item.id, subtasks);
    const hasChildren = lookup.children.length > 0;
    const isExpanded = hasChildren && (expanded?.has(item.id) ?? false);

    rows.push({
      ticket: item,
      kind: 'root',
      depth: 0,
      childCount: lookup.count,
      hasChildren,
      expanded: isExpanded,
      countIsFloor: lookup.countIsFloor,
      childrenIncomplete: lookup.incomplete,
      parentKey: null,
    });

    if (!isExpanded) continue;

    for (const child of lookup.children) {
      rows.push({
        ticket: child,
        kind: 'child',
        depth: 1,
        childCount: null,
        hasChildren: false,
        expanded: false,
        countIsFloor: false,
        childrenIncomplete: false,
        parentKey: item.key,
      });
    }
  }

  return rows;
}

/**
 * How many of the page's own rows ended up under a parent rather than at the top level.
 *
 * ⭐ Used by the pagination line. The server counts a sub-task as one of the 50 items on the page, so
 * without this "1–50 of 486" sits above 47 visible rows and the reader is left to wonder which number
 * is lying. ⛔ The answer is not to recompute `total` client-side — it is to say where the other
 * three went.
 */
export function countNestedOnPage(items: readonly TicketListItem[]): number {
  const roots = rootIdsOf(items);
  return items.filter((item) => item.parent_id !== null && roots.has(item.parent_id)).length;
}

/** Rows drawn at the top level that are really sub-tasks whose parent is on another page. */
export function countDetached(rows: readonly BacklogRow[]): number {
  return rows.filter((row) => row.kind === 'detached').length;
}
